import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navigation = [
  { name: 'Forside', href: '/' },
  { name: 'Julegaver', href: '/#julegaver' },
  { name: 'Promotion', href: '/kategori/firmagaver-jul' },
  {
    name: 'Beklædning',
    href: '#',
    dropdown: [
      { name: 'Profiltøj', href: '/kategori/profiltoej' },
      { name: 'Sportstøj', href: '/kategori/sportstoej' },
      { name: 'Arbejdstøj', href: '/kategori/arbejdstoej' },
      { name: 'Firmatøj', href: '/kategori/firmatoej' },
      { name: 'Jagt/outdoor', href: '/kategori/jagt-outdoor' },
    ]
  },
  { name: 'Tryk - Broderi', href: '/kategori/tryk-broderi' },
  { name: 'Messesystemer', href: '/messesystemer' },
  { name: 'Specialdesign', href: '/specialdesign' },
  { name: 'Anbefalinger', href: '/anbefalinger' },
  { name: 'Kontakt', href: '/kontakt' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [mobileBeklaedningOpen, setMobileBeklaedningOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header className={`bg-white sticky top-0 z-50 border-b border-gray-100 transition-shadow duration-200 ${
      isScrolled ? 'shadow-md' : ''
    }`}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-4">
        {/* Top Row: Logo & CTA */}
        <div className="flex items-center justify-between">
          <div className="flex lg:flex-1">
            <Link to="/" className="inline-block">
              <img 
                src="https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/4c1594c5-9a06-4fbd-82c2-848fc278da5d.jpeg" 
                alt="HN Promotion" 
                className="h-16 w-auto"
              />
            </Link>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-none p-2.5 text-gray-900"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Åbn menu</span>
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="hidden lg:flex lg:flex-1 lg:justify-end">
            <Link to="/showroom" className="text-xs font-bold uppercase tracking-wider leading-6 text-white bg-orange-500 px-5 py-2.5 rounded-none hover:bg-orange-600 transition-all">
              Besøg Showroom
            </Link>
          </div>
        </div>

        {/* Bottom Row: Menu Links (Desktop Only) */}
        <div className="hidden lg:flex lg:mt-6 lg:gap-x-10">
          {navigation.map((item) => {
            if (item.dropdown) {
              const isAnyActive = item.dropdown.some(sub => location.pathname === sub.href);
              return (
                <div key={item.name} className="relative group">
                  <button
                    className={`flex items-center gap-x-1 text-sm font-bold uppercase tracking-wider leading-6 transition-colors cursor-pointer ${
                      isAnyActive ? 'text-black' : 'text-gray-500 hover:text-black'
                    }`}
                  >
                    {item.name}
                    <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className="absolute left-0 top-full z-10 mt-2 w-48 origin-top-left bg-white shadow-lg ring-1 ring-black/5 focus:outline-none transition-all opacity-0 invisible group-hover:opacity-100 group-hover:visible duration-200">
                    <div className="py-1">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={`block px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                            location.pathname === subItem.href
                              ? 'bg-gray-50 text-black'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-black'
                          }`}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <a
                key={item.name}
                href={item.href}
                className={`text-sm font-bold uppercase tracking-wider leading-6 transition-colors ${
                  location.pathname === item.href ? 'text-black' : 'text-gray-500 hover:text-black'
                }`}
              >
                {item.name}
              </a>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-50 bg-white px-6 py-6 lg:hidden overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <Link to="/" className="inline-block" onClick={() => setMobileMenuOpen(false)}>
                <img 
                  src="https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/4c1594c5-9a06-4fbd-82c2-848fc278da5d.jpeg" 
                  alt="HN Promotion" 
                  className="h-12 w-auto"
                />
              </Link>
              <button
                type="button"
                className="-m-2.5 rounded-none p-2.5 text-gray-900"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Luk menu</span>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-100">
                <div className="space-y-2 py-6">
                  {navigation.map((item) => {
                    if (item.dropdown) {
                      const isAnyActive = item.dropdown.some(sub => location.pathname === sub.href);
                      const isOpen = mobileBeklaedningOpen;
                      const toggleOpen = () => {
                        setMobileBeklaedningOpen(!mobileBeklaedningOpen);
                      };
                      return (
                        <div key={item.name} className="space-y-1">
                          <button
                            onClick={toggleOpen}
                            className="flex w-full items-center justify-between rounded-none px-3 py-2 text-base font-bold uppercase tracking-wider leading-7 text-gray-900 hover:bg-gray-50"
                          >
                            <span className={isAnyActive ? 'text-orange-500' : ''}>{item.name}</span>
                            <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                          </button>
                          
                          <AnimatePresence>
                            {isOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden pl-4 space-y-1"
                              >
                                {item.dropdown.map((subItem) => (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.href}
                                    className={`block rounded-none px-3 py-2 text-sm font-bold uppercase tracking-wider leading-7 ${
                                      location.pathname === subItem.href
                                        ? 'text-orange-500 bg-gray-50'
                                        : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    {subItem.name}
                                  </Link>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    }

                    return (
                      <a
                        key={item.name}
                        href={item.href}
                        className="-mx-3 block rounded-none px-3 py-2 text-base font-bold uppercase tracking-wider leading-7 text-gray-900 hover:bg-gray-50"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </a>
                    );
                  })}
                </div>
                <div className="py-6">
                  <Link
                    to="/showroom"
                    className="-mx-3 block rounded-none px-3 py-2.5 text-base font-bold uppercase tracking-wider leading-7 text-white bg-orange-500 text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Besøg Showroom
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
